import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Plus, Search, Eye, Edit3, Trash2, Copy, ExternalLink, Layers } from "lucide-react";
import { toast } from "sonner";

export default function ProposalsListPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchProposals(); }, []);

  const fetchProposals = async () => {
    try {
      const { data } = await axios.get(`${API}/proposals`, { headers });
      setProposals(data);
    } catch { toast.error("Failed to load proposals"); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await axios.delete(`${API}/proposals/${id}`, { headers });
      toast.success("Deleted");
      fetchProposals();
    } catch { toast.error("Delete failed"); }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await axios.post(`${API}/proposals/${id}/duplicate`, {}, { headers });
      toast.success(`Duplicated: ${data.proposal_id}`);
      fetchProposals();
    } catch { toast.error("Duplicate failed"); }
  };

  const filtered = proposals.filter(p =>
    !search || p.hotel_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.proposal_id?.toLowerCase().includes(search.toLowerCase())
  );

  const statusColor = (s) => {
    if (s === "active") return "bg-emerald-500/15 text-emerald-400";
    if (s === "draft") return "bg-amber-500/15 text-amber-400";
    return "bg-zinc-500/15 text-zinc-400";
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="proposals-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white" data-testid="proposals-title">Proposals</h1>
          <p className="text-white/40 text-sm mt-1">{proposals.length} proposals total</p>
        </div>
        <button onClick={() => navigate("/proposals/new")}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A96E] text-[#030303] rounded-lg text-sm font-semibold hover:bg-[#D4B87A] transition-colors"
          data-testid="new-proposal-btn">
          <Plus className="w-4 h-4" /> New Proposal
        </button>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search proposals..."
          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-white/25 focus:border-[#C9A96E]/40 focus:outline-none"
          data-testid="search-proposals" />
      </div>

      <div className="space-y-2">
        {filtered.map(p => (
          <div key={p.id} className="flex items-center gap-4 p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:bg-white/[0.05] transition-colors group"
            data-testid={`proposal-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-white font-medium text-sm truncate">{p.hotel_name || "Untitled"}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor(p.status)}`}>{p.status}</span>
                {p.sections && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#C9A96E]/15 text-[#C9A96E]">V2</span>}
              </div>
              <div className="flex items-center gap-3 text-white/30 text-xs">
                <span>{p.proposal_id}</span>
                {p.location && <span>{p.location}</span>}
                <span>{p.views || 0} views</span>
                <span>{p.conversions || 0} conversions</span>
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {p.sections && (
                <button onClick={() => navigate(`/proposals/${p.id}/sections`)} title="Edit V2 Sections"
                  className="p-2 text-[#C9A96E]/60 hover:text-[#C9A96E] hover:bg-[#C9A96E]/10 rounded-lg transition-colors"
                  data-testid={`sections-btn-${p.id}`}>
                  <Layers className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => navigate(`/proposals/${p.id}/edit`)} title="Edit"
                className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid={`edit-btn-${p.id}`}>
                <Edit3 className="w-4 h-4" />
              </button>
              <a href={`/p/${p.proposal_id}`} target="_blank" rel="noopener noreferrer" title="Preview"
                className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid={`preview-btn-${p.id}`}>
                <ExternalLink className="w-4 h-4" />
              </a>
              <button onClick={() => handleDuplicate(p.id)} title="Duplicate"
                className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid={`duplicate-btn-${p.id}`}>
                <Copy className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(p.id)} title="Delete"
                className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                data-testid={`delete-btn-${p.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-white/25 text-sm">
            {search ? "No proposals match your search" : "No proposals yet"}
          </div>
        )}
      </div>
    </div>
  );
}
